import { LayoutTemplate, PenSquare, Rows3, Type } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage } from "../../types";

interface BuilderCustomPageSettingsProps {
  page: ProposalDocumentLayoutPage;
  readOnly?: boolean;
  onChange: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  className?: string;
}

type CustomPageSettings = Record<string, string | boolean | number | null>;

function getSettings(page: ProposalDocumentLayoutPage): CustomPageSettings {
  return page.settings && typeof page.settings === "object"
    ? (page.settings as CustomPageSettings)
    : {};
}

function getStringSetting(
  settings: CustomPageSettings,
  key: string,
  fallback = ""
) {
  const value = settings[key];
  return typeof value === "string" ? value : fallback;
}

function getBooleanSetting(
  settings: CustomPageSettings,
  key: string,
  fallback: boolean
) {
  const value = settings[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateSetting(
  settings: CustomPageSettings,
  key: string,
  value: string | boolean | number | null
) {
  return {
    settings: {
      ...settings,
      [key]: value,
    },
  };
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  icon,
  hint,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {icon}
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />

      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

export function BuilderCustomPageSettings({
  page,
  readOnly = false,
  onChange,
  className,
}: BuilderCustomPageSettingsProps) {
  const settings = getSettings(page);

  const title = getStringSetting(settings, "title", page.title ?? "Custom Page");
  const pagePurpose = getStringSetting(settings, "pagePurpose");
  const introText = getStringSetting(settings, "introText");
  const layoutIntent = getStringSetting(settings, "layoutIntent", "flexible");
  const anchorId = getStringSetting(settings, "anchorId");
  const showPageTitle = getBooleanSetting(settings, "showPageTitle", true);
  const allowProposalOverrides = getBooleanSetting(
    settings,
    "allowProposalOverrides",
    true
  );
  const isOptional = getBooleanSetting(settings, "isOptional", false);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-white/10 via-white/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <LayoutTemplate className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              Custom Page Settings
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Define the role, framing, and flexibility of this freeform page
              before shaping its sections and blocks on the stage.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Page framing"
          description="Set the visible title and optional intro that introduces this custom page."
        >
          <div>
            <FieldLabel
              label="Page title"
              icon={<Type className="h-4 w-4" />}
              hint="Visible title shown when this custom page renders."
            />
            <input
              value={title}
              onChange={(e) =>
                onChange(updateSetting(settings, "title", e.target.value))
              }
              disabled={readOnly}
              placeholder="Custom Page"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Intro text"
              hint="Optional opening copy that frames the page before the main content."
            />
            <textarea
              rows={4}
              value={introText}
              onChange={(e) =>
                onChange(updateSetting(settings, "introText", e.target.value))
              }
              disabled={readOnly}
              placeholder="Optional opening copy for this custom page"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Page purpose"
          description="Clarify what this custom page is meant to do inside the proposal flow."
        >
          <div>
            <FieldLabel
              label="Page purpose"
              icon={<PenSquare className="h-4 w-4" />}
              hint="Capture the purpose of the page so the structure stays intentional."
            />
            <input
              value={pagePurpose}
              onChange={(e) =>
                onChange(updateSetting(settings, "pagePurpose", e.target.value))
              }
              disabled={readOnly}
              placeholder="Explain what this page is for"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
            <p className="text-xs leading-5 text-white/40">
              Use this to define the role of the page, such as proof points,
              project-specific explanation, onboarding steps, or a one-off CTA.
            </p>
          </div>
        </SectionCard>

        <SectionCard
          title="Layout intent"
          description="Choose the general direction this page should lean toward as you build it."
        >
          <div>
            <FieldLabel
              label="Layout intent"
              icon={<Rows3 className="h-4 w-4" />}
              hint="This helps guide the tone and composition of the page."
            />
            <select
              value={layoutIntent}
              onChange={(e) =>
                onChange(updateSetting(settings, "layoutIntent", e.target.value))
              }
              disabled={readOnly}
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            >
              <option value="flexible" className="bg-[#111]">
                Flexible
              </option>
              <option value="content_heavy" className="bg-[#111]">
                Content heavy
              </option>
              <option value="visual" className="bg-[#111]">
                Visual
              </option>
              <option value="summary" className="bg-[#111]">
                Summary
              </option>
              <option value="cta" className="bg-[#111]">
                Call to action
              </option>
            </select>
          </div>

          <div>
            <FieldLabel
              label="Anchor ID / internal key"
              hint="Optional internal identifier for linking, mapping, or future logic."
            />
            <input
              value={anchorId}
              onChange={(e) =>
                onChange(updateSetting(settings, "anchorId", e.target.value))
              }
              disabled={readOnly}
              placeholder="custom-page-1"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Page behavior"
          description="Choose how visible and flexible this custom page should be across proposal usage."
        >
          <ToggleField
            label="Show page title"
            description="Display the custom page title inside the rendered page."
            checked={showPageTitle}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showPageTitle", checked))
            }
          />

          <ToggleField
            label="Allow proposal overrides"
            description="Let proposal-level edits change this page without changing the broader template structure."
            checked={allowProposalOverrides}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "allowProposalOverrides", checked))
            }
          />

          <ToggleField
            label="Optional page"
            description="Mark this custom page as optional within the broader document flow."
            checked={isOptional}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "isOptional", checked))
            }
          />
        </SectionCard>
      </div>
    </div>
  );
}